/**
 * Drill-down d'un widget catégoriel : cliquer sur une barre / une part descend
 * dans la dimension suivante (pays → région → ville), le fil d'Ariane remonte.
 *
 * Porté de Metrix (`frontend/src/hooks/useDrillDown.ts`), y compris l'opérateur de
 * filtre : `'exact'` est bien celui qu'attend le moteur Orion.
 */
import { useCallback, useState } from 'react';
import { executeQuery } from './queryService';
import { type FilterOperator } from './filterOperators';
import type { Row } from './useWidgetData';

/**
 * Égalité sur une dimension. SOURCE DE VÉRITÉ : `ALLOWED_OPS` dans
 * `backend/src/engine/security.ts` — `'='` n'y figure PAS et fait échouer la
 * requête en 400 (`Invalid operator: =`). Le moteur mappe `exact` sur `EXACT`
 * (GA4) et sur `=` (GAQL, côté Ads) : la traduction est son travail, pas le nôtre.
 */
export const DRILL_FILTER_OPERATOR: FilterOperator = 'exact';

/** Un niveau empilé : la dimension filtrée et la valeur cliquée. */
export interface DrillStep {
  dimension: string;
  value: string;
}

/** Une ligne du niveau courant de drill-down. Coords optionnelles pour le mode bulles (villes). */
export interface DrillItem {
  key: string;
  value: number;
  _lat?: number | null;
  _lng?: number | null;
}

export interface DrillDownConfig {
  enabled: boolean;
  /** Dimension du widget : sert de champ de filtre au premier clic (niveau 0). */
  dimension: string;
  /** Niveaux de descente suivants, dans l'ordre (ex. `['region', 'city']`). */
  drillDimensions: string[];
  dataSourceId: string;
  metric: string;
  topN: number;
  period: { start: string; end: string };
}

export interface DrillDownResult {
  /** Items du niveau courant, ou `null` quand on est au niveau 0 (données du widget). */
  drillItems: DrillItem[] | null;
  drillPath: DrillStep[];
  isLoading: boolean;
  /** Message d'échec de la dernière requête de drill (400, 500, timeout…), sinon `null`. */
  error: string | null;
  canDrillDown: boolean;
  handleDrillDown: (value: string) => Promise<void>;
  handleDrillUp: () => void;
  /**
   * Saute à une profondeur donnée (`depth` = nombre de niveaux CONSERVÉS).
   * `drillTo(0)` revient aux données propres du widget. Indispensable au fil
   * d'Ariane : à profondeur ≥ 2, une simple remontée d'un cran ne ramène pas à
   * « Tout ».
   */
  drillTo: (depth: number) => void;
  /** Vide la pile (équivalent `drillTo(0)`). */
  resetDrill: () => void;
}

const NOOP_ASYNC = async () => {};
const NOOP = () => {};
const NOOP_DEPTH = (_depth: number) => {};
const EMPTY_PATH: DrillStep[] = [];

const DRILL_ERROR_FALLBACK = 'Impossible de charger ce niveau de détail.';

export function useDrillDown(cfg: DrillDownConfig): DrillDownResult {
  // Règle des hooks : tous les hooks sont appelés inconditionnellement,
  // l'early return `!enabled` vient APRÈS.
  const [drillPath, setDrillPath] = useState<DrillStep[]>([]);
  const [drillItems, setDrillItems] = useState<DrillItem[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On peut descendre tant qu'il reste des niveaux dans `drillDimensions`.
  const canDrillDown = cfg.enabled && drillPath.length < cfg.drillDimensions.length;

  /**
   * Charge un niveau. Ne jette JAMAIS : renvoie `false` si la requête a échoué, à
   * charge de l'appelant de restaurer la pile d'avant le clic — sans quoi le fil
   * d'Ariane annoncerait un niveau que le widget n'affiche pas.
   */
  const fetchLevel = useCallback(
    async (path: DrillStep[]): Promise<boolean> => {
      if (!cfg.enabled || cfg.drillDimensions.length === 0) return true;

      // `path.length - 1` = index de la dimension à interroger.
      const nextDimIndex = path.length - 1;
      if (nextDimIndex < 0 || nextDimIndex >= cfg.drillDimensions.length) return true;

      setIsLoading(true);
      setError(null);
      try {
        const filters = path.map((step) => ({
          field: step.dimension,
          operator: DRILL_FILTER_OPERATOR,
          value: step.value,
        }));
        const nextDim = cfg.drillDimensions[nextDimIndex];

        const result = await executeQuery({
          data_source_id: cfg.dataSourceId,
          metrics: [cfg.metric],
          dimensions: [nextDim],
          filters: filters.length ? filters : undefined,
          start: cfg.period.start,
          end: cfg.period.end,
        });

        const items: DrillItem[] = (result.current as Row[])
          .map((row) => ({
            key: String(row[nextDim] ?? ''),
            value: Number(row[cfg.metric]) || 0,
            _lat: row['_lat'] != null ? Number(row['_lat']) : null,
            _lng: row['_lng'] != null ? Number(row['_lng']) : null,
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, cfg.topN);

        setDrillItems(items);
        return true;
      } catch (err) {
        setError(err instanceof Error && err.message ? err.message : DRILL_ERROR_FALLBACK);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [
      cfg.enabled,
      cfg.drillDimensions,
      cfg.dataSourceId,
      cfg.metric,
      cfg.topN,
      cfg.period.start,
      cfg.period.end,
    ]
  );

  const handleDrillDown = useCallback(
    async (value: string) => {
      if (!canDrillDown) return;
      // Niveau 0 : on filtre sur la dimension du widget. Plus bas : sur la dimension de drill précédente.
      const filterDim =
        drillPath.length === 0 ? cfg.dimension : cfg.drillDimensions[drillPath.length - 1];
      const previousPath = drillPath;
      const newPath = [...drillPath, { dimension: filterDim, value }];
      setDrillPath(newPath);
      // Échec (400, 500, timeout…) : on rembobine la pile — `drillItems` est resté
      // sur le niveau précédent, la pile doit le refléter.
      if (!(await fetchLevel(newPath))) setDrillPath(previousPath);
    },
    [canDrillDown, cfg.dimension, cfg.drillDimensions, drillPath, fetchLevel]
  );

  /** `depth` = nombre de niveaux conservés (0 = tout réinitialiser). */
  const drillTo = useCallback(
    (depth: number) => {
      if (depth < 0 || depth >= drillPath.length) return;
      const previousPath = drillPath;
      const newPath = drillPath.slice(0, depth);
      setDrillPath(newPath);
      if (newPath.length === 0) {
        setDrillItems(null);
        setError(null);
      } else {
        void fetchLevel(newPath).then((ok) => {
          if (!ok) setDrillPath(previousPath);
        });
      }
    },
    [drillPath, fetchLevel]
  );

  const handleDrillUp = useCallback(() => {
    drillTo(drillPath.length - 1);
  }, [drillTo, drillPath.length]);

  const resetDrill = useCallback(() => {
    drillTo(0);
  }, [drillTo]);

  if (!cfg.enabled) {
    return {
      drillItems: null,
      drillPath: EMPTY_PATH,
      isLoading: false,
      error: null,
      canDrillDown: false,
      handleDrillDown: NOOP_ASYNC,
      handleDrillUp: NOOP,
      drillTo: NOOP_DEPTH,
      resetDrill: NOOP,
    };
  }

  return {
    drillItems,
    drillPath,
    isLoading,
    error,
    canDrillDown,
    handleDrillDown,
    handleDrillUp,
    drillTo,
    resetDrill,
  };
}
